/**
 * The one normalization rule for an email used as an identity key: trim the
 * surrounding whitespace, put the address into Unicode NFC, and lowercase it.
 *
 * Returns `null` when the input cannot be an address at all. This is not an
 * RFC 5322 validator and is not trying to be — routes validate shape via
 * Schema before they get here. It rejects only what would make a meaningless
 * lookup key, so that a normalized value is always something a person could
 * have typed into a login box.
 *
 * ## Normalized emails are compared as bytes
 *
 * The value this returns is the key, and two keys are the same person exactly
 * when their bytes are identical. Every fold happens here, in JavaScript. The
 * database stores what it is given and compares it verbatim; it must never
 * re-derive the key, and nothing may be matched with `lower()`, `citext` or a
 * case-insensitive collation.
 *
 * That is a constraint, not a preference, because `lower()` does not agree
 * with JavaScript beyond ASCII and the two disagree in both directions:
 * `lower('İ')` is a bare `i` under a glibc collation where JavaScript gives
 * `i` + U+0307, and `lower('ΣΟΦΟΣ')` ends in a medial sigma where JavaScript
 * gives a final one. A key derived on one side and matched on the other is a
 * lockout — the row exists and the lookup misses it.
 *
 * Byte comparison is what a UNIQUE index already does under a deterministic
 * collation, so the constraint costs nothing to hold. It is broken by putting
 * a nondeterministic collation on the column, which would make two distinct
 * keys compare equal.
 *
 * NFC is what makes byte comparison safe: `é` typed as U+00E9 and as
 * `e` + U+0301 are one address and have to reach the index as one string.
 *
 * It runs **after** lowercasing, and the order is load-bearing. Lowercasing
 * creates newly-composable sequences, so composing first leaves a key that
 * composing again would change: `H` + U+0331 has no precomposed form and
 * survives NFC, lowercases to `h` + U+0331, and that does compose, to U+1E96.
 * Folding in that order is not idempotent for 22 of the 183,840 base-plus-mark
 * combinations below U+3000, which would make a key fail a check against
 * itself. Lowercasing first is idempotent for all of them.
 *
 * ## Two things this deliberately does not do
 *
 * Provider-specific folding — Gmail's dots, plus-addressing — is not applied.
 * `a.b+x@gmail.com` and `ab@gmail.com` stay distinct keys. Folding them would
 * merge two rows into one person, and merging the wrong two people is the
 * exact failure the conflicts table exists to prevent; a false split costs a
 * manual resolution, a false merge costs someone else's EAP history.
 *
 * The local part is lowercased even though RFC 5321 says it is case-sensitive.
 * Every login path in both apps looks up a lowercased address, so preserving
 * local-part case would make two accounts reachable that login treats as one.
 *
 * Lowercasing is Unicode default case conversion, not the full case folding of
 * RFC 8265's `UsernameCaseMapped` profile, so it does not unify every spelling
 * of the same name: a Greek address written with a final sigma and the same
 * address written with a medial one are two keys. No JavaScript runtime ships
 * case folding, and `CASE_FOLDING_CORPUS` pins what this function does instead.
 */
export const normalizeEmail = (input) => {
    const trimmed = input.trim();
    if (trimmed.length === 0)
        return null;
    // Any remaining whitespace means this was never one address.
    if (/\s/.test(trimmed))
        return null;
    // Exactly one separator, with something on each side of it.
    const at = trimmed.indexOf("@");
    if (at <= 0)
        return null;
    if (at !== trimmed.lastIndexOf("@"))
        return null;
    if (at === trimmed.length - 1)
        return null;
    // The only place a `NormalizedEmail` is minted: every branch above has
    // rejected what cannot be one, so this is where the invariant is
    // established rather than asserted.
    return trimmed.toLowerCase().normalize("NFC");
};
/**
 * Whether this string is already its own normalized form, and may therefore be
 * used as an identity key without being folded again.
 *
 * This is the checked way into `NormalizedEmail` for a value that did not come
 * from `normalizeEmail`: a key read back out of a `normalized_email` column, or
 * one handed over an internal boundary that already folded it. It is a real
 * test rather than an assertion — a string normalization would change is not
 * narrowed, so a caller holding an unfolded address has to deal with it instead
 * of being allowed to assume.
 */
export const isNormalized = (value) => normalizeEmail(value) === value;
const NON_ASCII = /\P{ASCII}/u;
// RFC 5321 atext, ASCII only; any non-ASCII character is atext under RFC 6531.
const ATEXT = /^[\w!#$%&'*+\-/=?^`{|}~]+$/;
// Stricter than the retired check: whitespace (Unicode included) and control
// characters make an address no mail system will deliver.
const UNUSABLE_IN_LOCAL = /[\s\p{Cc}]/u;
// Stricter than the retired check: characters IDNA silently drops, so the
// stored spelling would carry invisible characters the mail never sees.
const isIgnoredInDomain = (codePoint) => codePoint === 0xad ||
    codePoint === 0x34f ||
    (codePoint >= 0x115f && codePoint <= 0x1160) ||
    (codePoint >= 0x17b4 && codePoint <= 0x17b5) ||
    (codePoint >= 0x180b && codePoint <= 0x180f) ||
    codePoint === 0x200b ||
    (codePoint >= 0x2060 && codePoint <= 0x2064) ||
    (codePoint >= 0x206a && codePoint <= 0x206f) ||
    codePoint === 0x3164 ||
    (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
    codePoint === 0xfeff ||
    codePoint === 0xffa0 ||
    (codePoint >= 0x1bca0 && codePoint <= 0x1bca3) ||
    (codePoint >= 0x1d173 && codePoint <= 0x1d17a) ||
    (codePoint >= 0xe0100 && codePoint <= 0xe01ef);
const DOMAIN_CONTROL = /[\p{Cc} @:/\\#!$&'()*+,;=?]/u;
const DOMAIN_LABEL = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const TOP_LEVEL_LABEL = /^[a-zA-Z](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const encoder = new TextEncoder();
/** The domain as DNS sees it: IDNA-converted by the WHATWG URL parser. */
const asciiDomain = (domain) => {
    try {
        return new URL(`http://${domain.replace(/%/g, "%25")}`).host;
    }
    catch {
        return domain;
    }
};
const isDomain = (raw) => {
    if (raw.length === 0 || raw.length > 256)
        return false;
    if ([...raw].some((char) => isIgnoredInDomain(char.codePointAt(0) ?? 0))) {
        return false;
    }
    const domain = NON_ASCII.test(raw) ? raw.normalize("NFC") : raw;
    if (DOMAIN_CONTROL.test(domain))
        return false;
    const labels = asciiDomain(domain).split(".");
    if (labels.length < 2)
        return false;
    return labels.every((label, index) => label.length > 0 &&
        label.length <= 63 &&
        (index < labels.length - 1 ? DOMAIN_LABEL : TOP_LEVEL_LABEL).test(label));
};
/**
 * Whether a string is an address an account can be created for. The rule is
 * the one the retired Joi `.email()` check applied (its parser,
 * `@sideway/address`): the address is read in NFC; at most 254 characters, a
 * local part of at most 64 UTF-8 bytes made of dot-separated atoms, and a
 * domain of at least two labels that the WHATWG URL parser converts to an
 * IDNA (Punycode) form of letter-digit-hyphen labels of at most 63 each, the
 * last starting with a letter. Internationalized local parts and domains are
 * accepted. Two things are deliberately stricter than that check: whitespace
 * or control characters in the local part, and characters IDNA drops (soft
 * hyphen, zero-width space and the like) in the domain, are refused. Top-level
 * labels are not checked against the IANA list.
 *
 * This is the shape check account-creating routes apply before anything is
 * stored. `normalizeEmail` is the separate, looser rule for turning an input
 * into an identity key.
 */
export const isEmailAddress = (value) => {
    if (value.length === 0)
        return false;
    const nonAscii = NON_ASCII.test(value);
    const email = nonAscii ? value.normalize("NFC") : value;
    const parts = email.split("@");
    if (parts.length !== 2)
        return false;
    const [local = "", domain = ""] = parts;
    if (local.length === 0 || email.length > 254)
        return false;
    if (encoder.encode(local).length > 64)
        return false;
    if (UNUSABLE_IN_LOCAL.test(local))
        return false;
    const atomsValid = local
        .split(".")
        .every((atom) => atom.length > 0 &&
        (nonAscii
            ? [...atom].every((char) => ATEXT.test(char) || NON_ASCII.test(char))
            : ATEXT.test(atom)));
    return atomsValid && isDomain(domain);
};
/** Folds an address to its key and checks it can hold an account, or null. */
export const accountEmail = (input) => {
    const key = normalizeEmail(input);
    return key !== null && isEmailAddress(key) ? key : null;
};
/** Whether a string is already an `AccountEmail`, unfolded input refused. */
export const isAccountEmail = (value) => accountEmail(value) === value;
/**
 * The spellings an account with this key may be stored under, each compared
 * byte for byte: the key itself, and its decomposed (NFD) form. The creators
 * that do not build an `AccountEmail` (the user-sync processor, SSO
 * registration, `/items`, the CLI) store the address lowercased as typed, so
 * one typed decomposed is held decomposed; account lookups match it too
 * rather than creating a second account beside it.
 */
export const accountEmailSpellings = (key) => {
    const decomposed = key.normalize("NFD");
    return decomposed === key ? [key] : [key, decomposed];
};
/**
 * The inputs that pin `normalizeEmail`'s output. The ASCII entries fix the
 * everyday behaviour; the rest are the points where a database collation would
 * fold differently, and are here so that a change to this function which moved
 * any of them has to be a deliberate edit to a committed expectation.
 */
export const CASE_FOLDING_CORPUS = [
    "Person@Example.test",
    "PERSON@EXAMPLE.TEST",
    "person@example.test",
    // Latin-1 accents: single-code-point mappings.
    "Renée@Example.test",
    "ÄÖÜ@example.test",
    // The same address decomposed — NFC has to put it back together.
    "Rene\u0301e@Example.test",
    // German sharp s. Capital U+1E9E lowercases to U+00DF under Unicode default
    // case conversion; a collation need not do the same.
    "STRASSEẞ@example.test",
    // Turkish dotted capital I: lowercases to two code points (i + combining
    // dot above) here, to a bare `i` under a glibc collation.
    "İstanbul@example.test",
    // Dotless lowercase i, the other half of the Turkish pair.
    "ıstanbul@example.test",
    // A base whose precomposed form exists only in lowercase: NFC leaves the
    // capital decomposed, and composing before lowercasing would give a key that
    // normalizing again would change.
    "H\u0331@example.test",
    // Greek: a trailing capital sigma lowercases to a final sigma, which is a
    // different key from the same name spelled with a medial one.
    "ΣΟΦΟΣ@example.test",
    "σοφοσ@example.test",
];
//# sourceMappingURL=email.js.map